import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { useSearch } from '@/src/contexts/SearchContext';
import { FeatureType } from '@/src/types';
import DebouncedInput from '@/src/components/DebouncedInput';
import SuggestionDropdown from '@/src/components/SuggestionDropdown';
import { formatLargeBudget } from '@/src/utils/formatters';
import { db, doc, updateDoc, handleFirestoreError, OperationType } from '../firebase';

interface LaunchpadProps {
  activeCurrency: { code: string; name: string; symbol: string } | string;
  filteredCurrencies: ({ code: string; name: string; symbol: string } | string)[];
  exchangeRates: Record<string, number>;
  currentMinBudget: number;
  currentMaxBudget: number;
  launchpadLocRef: React.RefObject<HTMLInputElement | null>;
  user: any;
}

const Launchpad: React.FC<LaunchpadProps> = ({
  activeCurrency,
  filteredCurrencies,
  exchangeRates,
  currentMinBudget,
  currentMaxBudget,
  launchpadLocRef,
  user
}) => {
  const { 
    state, 
    dispatch, 
    handleRunScan, 
    handleLocInputChange, 
    handleLaunchpadSectorChange, 
    handleSuggestionSelect 
  } = useSearch();

  const currency = useMemo(() => {
    if (activeCurrency && typeof activeCurrency === 'object' && 'code' in activeCurrency) {
      return activeCurrency as { code: string; name: string; symbol: string };
    }
    return { code: typeof activeCurrency === 'string' ? activeCurrency : 'USD', name: 'US Dollar', symbol: '$' };
  }, [activeCurrency]);

  return (
    <div className="w-full max-w-[800px] mx-auto p-6 sm:p-10 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6 md:space-y-8 relative overflow-hidden h-full min-h-[580px] flex flex-col text-left">
      <div className="space-y-6 flex-1 relative z-10">
        <div className="relative inline-block">
          <button 
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch({ type: 'SET_MENU_OPEN', payload: { menu: 'currency', isOpen: !state.isCurrencyMenuOpen } }); }} 
            className="flex items-center gap-3 px-3.5 py-2 bg-[#322D29] border border-white/[0.08] rounded-lg hover:border-[#F26522]/60 transition-all cursor-pointer"
          >
            <div className="w-7 h-7 bg-[#F26522] rounded flex items-center justify-center text-[#FFFFFF] font-bold text-xs">
              {currency.symbol}
            </div>
            <div className="flex flex-col items-start leading-none text-left min-w-[120px]">
              <span className="text-[8px] font-bold text-[#F26522] uppercase tracking-[0.2em] mb-1">Valuation Index</span>
              <span className="text-[11px] font-medium text-white uppercase tracking-wider">{currency.code} • {currency.name}</span>
            </div>
            <div className="h-4 w-px bg-white/10 mx-1"></div>
            <i className={`fas fa-chevron-down text-[9px] text-white/40 transition-transform ${state.isCurrencyMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {state.isCurrencyMenuOpen && (
            <div className="absolute left-0 top-full mt-2 w-80 bg-[#322D29] border border-white/[0.1] z-[200] p-4 rounded-xl overflow-hidden shadow-2xl">
              <header className="mb-3 px-1 py-1 flex justify-between items-center border-b border-white/[0.06] pb-2">
                <span className="text-[10px] font-medium uppercase tracking-widest text-white/60">Currency Protocol</span>
              </header>
              <div className="pb-3">
                <div className="relative">
                  <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-xs text-white/40"></i>
                  <DebouncedInput 
                    autoFocus
                    placeholder="SEARCH CURRENCY..." 
                    className="w-full pl-9 pr-4 py-2 bg-[#3a3430] border border-white/[0.08] rounded-lg text-xs font-medium uppercase tracking-wider outline-none focus:border-[#F26522] text-white placeholder:text-white/40 transition-all font-mono"
                    value={state.currencySearch}
                    onChange={val => dispatch({ type: 'SET_SEARCH_TERM', payload: { type: 'currency', term: val } })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1 max-h-72 overflow-y-auto custom-scrollbar p-1">
                {filteredCurrencies.length === 0 ? (
                  <div className="p-6 text-center">
                    <span className="text-xs text-white/40 uppercase tracking-wider">No match found</span>
                  </div>
                ) : filteredCurrencies.map((curr, idx) => {
                  if (!curr || (typeof curr !== 'string' && !curr.code)) return null;
                  const cObj = typeof curr === 'string' ? { code: curr, name: curr, symbol: '$' } : curr;
                  const isSelected = state.preferredCurrency === cObj.code;
                  return (
                    <button 
                      key={`launchpad-curr-${cObj.code}-${idx}`} 
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        const oldCurrency = state.preferredCurrency;
                        const newCurrency = cObj.code;
                        
                        let newBudget = state.budgetLimit;
                        if (exchangeRates[oldCurrency] && exchangeRates[newCurrency]) {
                          const usdAmount = state.budgetLimit / exchangeRates[oldCurrency];
                          newBudget = Math.round(usdAmount * exchangeRates[newCurrency]);
                        } else if (newCurrency === 'USD' && exchangeRates[oldCurrency]) {
                          newBudget = Math.round(state.budgetLimit / exchangeRates[oldCurrency]);
                        } else if (oldCurrency === 'USD' && exchangeRates[newCurrency]) {
                          newBudget = Math.round(state.budgetLimit * exchangeRates[newCurrency]);
                        }

                        dispatch({ type: 'SET_STATE', payload: { preferredCurrency: newCurrency, budgetLimit: newBudget } }); 
                        if (user) {
                          updateDoc(doc(db, 'users', user.uid), { 
                            preferredCurrency: newCurrency,
                            budgetLimit: newBudget
                          }).catch((err: any) => handleFirestoreError(err, OperationType.UPDATE, `users/${user.uid}`));
                        }
                        dispatch({ type: 'SET_MENU_OPEN', payload: { menu: 'currency', isOpen: false } }); 
                        dispatch({ type: 'SET_SEARCH_TERM', payload: { type: 'currency', term: '' } }); 
                      }} 
                      className={`text-left p-2.5 hover:bg-[#3a3430] rounded-lg transition-all flex items-center justify-between border cursor-pointer ${isSelected ? 'border-[#F26522] bg-[#F26522]/10' : 'border-transparent'}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded bg-[#3a3430] flex items-center justify-center font-bold text-white text-xs">
                          {cObj.symbol}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs font-medium text-white uppercase leading-none mb-1">{cObj.code}</span>
                          <span className="text-[10px] text-white/50 truncate uppercase tracking-wider">{cObj.name}</span>
                        </div>
                      </div>
                      {isSelected && <i className="fas fa-check text-[#F26522] text-xs"></i>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-[#F26522] flex items-center justify-center text-white font-bold text-sm shrink-0">
              L
            </div>
            <h3 className="text-xl font-medium text-white tracking-tight">Launchpad</h3>
          </div>
          <p className="text-xs text-white/60 leading-relaxed font-normal">
            Validate new ideas. Enter your concept, location, sector, and budget. Get startup costs, break-even timeline, risk scores, and exact site recommendations.
          </p>
        </div>
      </div>

      <div className="space-y-4 relative z-10">
        <div className="relative">
          <DebouncedInput 
            ref={launchpadLocRef as any} 
            placeholder="Target location (city, district, suburb)..." 
            className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] rounded-lg font-normal text-sm text-white focus:border-[#F26522] outline-none transition-all pr-12 placeholder:text-xs placeholder:text-white/40" 
            value={state.launchpadLocation} 
            onChange={val => handleLocInputChange(val, 'launchpad')} 
          />
          <button 
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch({ type: 'SET_MAP_PICKER', payload: { isOpen: true, target: 'launchpad' } }); }} 
            className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded flex items-center justify-center text-white/40 hover:text-[#F26522] hover:bg-white/5 transition-all cursor-pointer"
            title="Pick on map"
          >
            <i className="fas fa-location-crosshairs text-sm"></i>
          </button>
          <SuggestionDropdown 
            hub="launchpad" 
            isFetchingSuggestions={state.isFetchingSuggestions}
            locSuggestions={state.activeSuggestionHub === 'launchpad' ? state.locSuggestions : []}
            onSelect={(name) => handleSuggestionSelect(name, 'launchpad')}
            onClose={() => dispatch({ type: 'SET_SUGGESTIONS', payload: [] })}
          />
        </div>

        <div className="space-y-2">
          <DebouncedInput 
            placeholder="Business or sector (e.g., specialty bakery, co-working, clinic)..." 
            className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] rounded-lg font-normal text-sm text-white outline-none transition-all focus:border-[#F26522] placeholder:text-xs placeholder:text-white/40" 
            value={state.launchpadSector} 
            onChange={val => handleLaunchpadSectorChange(val)} 
            onKeyDown={(e: any) => e.key === 'Enter' && handleRunScan(FeatureType.Scan)} 
          />
          {state.launchpadSectorWarning && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[#322D29] border border-[#F26522] rounded-lg">
              <i className="fas fa-triangle-exclamation text-xs text-[#F26522]"></i>
              <span className="text-xs text-[#F26522] leading-tight">
                Alert: Category field detected location-like data. Did you mean to enter a business type?
              </span>
            </div>
          )}
        </div>
        
        <div className="p-4 bg-[#322D29] border border-white/[0.08] rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="fas fa-coins text-xs text-[#F26522]"></i>
              <span className="text-xs font-medium text-white">Match Capital Ceiling</span>
            </div>
            <button 
              onClick={() => dispatch({ type: 'SET_STATE', payload: { isBudgetActive: !state.isBudgetActive } })}
              className={`w-10 h-5 rounded-full p-0.5 transition-all duration-300 border border-white/10 cursor-pointer ${state.isBudgetActive ? 'bg-[#F26522]' : 'bg-white/10'}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-300 ${state.isBudgetActive ? 'translate-x-5' : 'translate-x-0'}`}></div>
            </button>
          </div>

          {state.isBudgetActive && (
            <div className="space-y-4 pt-1">
              <div className="relative group/input">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-white/50">
                  {currency.symbol}
                </div>
                <input 
                  type="text"
                  placeholder="Enter Amount..."
                  className="w-full pl-9 pr-24 py-2.5 bg-[#3a3430] border border-white/[0.08] rounded-lg text-white font-medium text-sm outline-none focus:border-[#F26522] transition-all placeholder:text-xs placeholder:text-white/40"
                  value={state.budgetLimit === 0 ? '' : state.budgetLimit.toLocaleString()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    dispatch({ type: 'SET_STATE', payload: { budgetLimit: num } });
                  }}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-[#F26522]/15 text-[#F26522] text-[10px] font-medium rounded">
                  ~ {formatLargeBudget(state.budgetLimit)}
                </div>
              </div>

              <div className="space-y-1 px-1">
                <input 
                  type="range"
                  min={currentMinBudget}
                  max={currentMaxBudget}
                  step={Math.max(1, Math.round(currentMinBudget / 5))}
                  value={Math.max(currentMinBudget, Math.min(state.budgetLimit, currentMaxBudget))}
                  onChange={(e) => dispatch({ type: 'SET_STATE', payload: { budgetLimit: parseInt(e.target.value) } })}
                  className="w-full h-1 bg-white/20 rounded appearance-none cursor-pointer accent-[#F26522]"
                />
                <div className="flex justify-between text-[10px] text-white/60">
                  <span>{formatLargeBudget(currentMinBudget)}</span>
                  <span className="text-[#F26522] font-medium">{formatLargeBudget(currentMaxBudget)} Limit</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto pt-4 relative z-10">
        <button 
          onClick={() => handleRunScan(FeatureType.Scan)} 
          disabled={!(state.launchpadLocation || state.launchpadSector) || state.loading} 
          className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-[#FFFFFF] py-3 rounded-lg font-medium text-xs uppercase tracking-wider transition-all disabled:opacity-30 flex items-center justify-center gap-2 cursor-pointer"
        >
          {state.loading && state.activeFeature === FeatureType.Scan ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white/80 animate-spin" />
              <span>Scanning Grid...</span>
            </>
          ) : (
            <>
              <span>{state.launchpadSector ? `Assess ${state.launchpadSector}` : 'Assess Market'}</span>
              <i className="fas fa-arrow-right text-xs"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Launchpad;
